import { User } from "@configcat/sdk/browser";
import { makeAutoObservable, runInAction } from "mobx";
import { ConfigCatKeys, getConfigCatValue } from "../../common/configCatClient";
import { logger } from "../../common/logger";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { Company } from "../../common/types/companyTypes";
import type { CustomerFeature, FEATURE_NAME } from "../../common/types/featureTypes";
import { keepIdOnly } from "../../common/utils/segmentUtils";
import type { Scenario } from "../../mocks/types";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";

type FeatureStoreType = BaseStoreType & {
   customerFeatures: FEATURE_NAME[];
   barcodeScannerEnabled: boolean;
   claimsEnabled: boolean;
   notificationsEnabled: boolean;
   newPromotionNotificationsEnabled: boolean;
   keyFiguresAvailable: boolean;
   recommendationType: "AI1" | "AI2" | "AI3" | "OFF";
   contractualRecommendations: boolean;
   voucherifyAvailable: boolean;
   loginAvailable: boolean;
   loginUnavailableText: string;
   productNavigationAvailable: boolean;
   useDynamicsForNotificationProfiles: boolean;
   newOrderFlowEnabled: boolean;
   liveDeliveryStatusEnabled: boolean;
   prioritizeAgreementAssortment: boolean;
   searchResultsSortedByCustomerGroupSales: boolean;
   labelsInQuickSearch: boolean;
   filterDisplayThreshold: number;
   mockScenarios: Scenario[];
   showEditDeliveryAddressButton: boolean;
   contentfulCachingEnabled: boolean;

   fetchEnabledConfigCatFeatures: (company?: Company) => Promise<void>;
   fetchEnabledCustomerFeatures: (customerNumber: string) => Promise<void>;
   hasCustomerFeature: (feature: FEATURE_NAME) => boolean;
};

export const featuresStore: FeatureStoreType = makeAutoObservable({
   customerFeatures: [],
   barcodeScannerEnabled: false,
   claimsEnabled: false,
   notificationsEnabled: false,
   newPromotionNotificationsEnabled: false,
   keyFiguresAvailable: false,
   recommendationType: "AI1",
   contractualRecommendations: false,
   voucherifyAvailable: false,
   loginAvailable: true,
   loginUnavailableText: "Innlogging er ikke tilgjengelig.",
   productNavigationAvailable: true,
   useDynamicsForNotificationProfiles: false,
   newOrderFlowEnabled: false,
   liveDeliveryStatusEnabled: false,
   prioritizeAgreementAssortment: false,
   searchResultsSortedByCustomerGroupSales: false,
   labelsInQuickSearch: false,
   filterDisplayThreshold: 8,
   mockScenarios: [],
   showEditDeliveryAddressButton: false,
   contentfulCachingEnabled: false,
   fetchEnabledConfigCatFeatures: async (company) => {
      const userData = new User(company?.customerNumber || "Anonymous");

      userData.custom = {
         chainLevel1: keepIdOnly(company?.businessLevels.level1 || "N/A"),
         chainLevel2: keepIdOnly(company?.businessLevels.level2 || "N/A"),
         chainLevel3: keepIdOnly(company?.businessLevels.level3 || "N/A"),
         chainLevel4: keepIdOnly(company?.businessLevels.level4 || "N/A"),
         customerGroup: keepIdOnly(company?.customerGroup || "N/A")
      };

      const flagsAndValues: Partial<FeatureStoreType> = {};

      flagsAndValues.barcodeScannerEnabled = await getConfigCatValue(ConfigCatKeys.BARCODESCANNER_AVAILABLE, userData);
      flagsAndValues.claimsEnabled = await getConfigCatValue(ConfigCatKeys.CLAIMS_AVAILABLE, userData);
      flagsAndValues.notificationsEnabled = await getConfigCatValue(ConfigCatKeys.NOTIFICATIONS_AVAILABLE, userData);
      flagsAndValues.newPromotionNotificationsEnabled = await getConfigCatValue(
         ConfigCatKeys.NEW_PROMOTION_NOTIFICATIONS_AVAILABLE,
         userData
      );
      flagsAndValues.recommendationType = await getConfigCatValue(ConfigCatKeys.RECOMMENDATION_TYPE, userData);
      flagsAndValues.keyFiguresAvailable = await getConfigCatValue(ConfigCatKeys.KEYFIGURES_AVAILABLE, userData);
      flagsAndValues.contractualRecommendations = await getConfigCatValue(ConfigCatKeys.CONTRACTUAL_RECOMMENDATIONS, userData);
      flagsAndValues.voucherifyAvailable = await getConfigCatValue(ConfigCatKeys.VOUCHERIFY_AVAILABLE, userData);
      flagsAndValues.loginAvailable = await getConfigCatValue(ConfigCatKeys.LOGIN_AVAILABLE, userData);
      flagsAndValues.loginUnavailableText = await getConfigCatValue(ConfigCatKeys.LOGIN_UNAVAILABLE_TEXT, userData);
      flagsAndValues.productNavigationAvailable = await getConfigCatValue(ConfigCatKeys.PRODUCT_NAVIGATION_AVAILABLE, userData);
      flagsAndValues.useDynamicsForNotificationProfiles = await getConfigCatValue(
         ConfigCatKeys.USE_DYNAMICS_FOR_NOTIFICATION_PROFILES,
         userData
      );
      flagsAndValues.newOrderFlowEnabled = await getConfigCatValue(ConfigCatKeys.NEW_ORDER_FLOW_ENABLED, userData);

      flagsAndValues.liveDeliveryStatusEnabled = await getConfigCatValue(ConfigCatKeys.LIVE_DELIVERY_STATUS_ENABLED, userData);
      flagsAndValues.prioritizeAgreementAssortment = await getConfigCatValue(
         ConfigCatKeys.PRIORITIZE_AGREEMENT_ASSORTMENT,
         userData
      );
      flagsAndValues.searchResultsSortedByCustomerGroupSales = await getConfigCatValue(
         ConfigCatKeys.SEARCH_RESULTS_SORTED_BY_CUSTOMER_GROUP_SALES,
         userData
      );
      flagsAndValues.filterDisplayThreshold = await getConfigCatValue(ConfigCatKeys.FILTER_DISPLAY_THRESHOLD, userData);
      flagsAndValues.labelsInQuickSearch = await getConfigCatValue(ConfigCatKeys.LABELS_IN_QUICK_SEARCH, userData);
      flagsAndValues.showEditDeliveryAddressButton = await getConfigCatValue(
         ConfigCatKeys.SHOW_EDIT_DELIVERY_ADDRESS_BUTTON,
         userData
      );
      flagsAndValues.contentfulCachingEnabled = await getConfigCatValue(ConfigCatKeys.CONTENTFUL_CACHING_ENABLED, userData);

      runInAction(() => {
         Object.assign(featuresStore, flagsAndValues);
      });
   },

   fetchEnabledCustomerFeatures: async (customerNumber) => {
      const resp: CustomerFeature[] = await theme.fetchCustomerFeatures(customerNumber, authStore.getSessionToken() || "");
      runInAction(() => {
         featuresStore.customerFeatures = resp.map((feature) => feature.name);
      });

      logger.log("auth", "Features found: ", featuresStore.customerFeatures);
   },

   hasCustomerFeature: (featureName) => {
      return featuresStore.customerFeatures.includes(featureName);
   },
   clearCompanySpecificData: () => {
      featuresStore.customerFeatures = [];
      void featuresStore.fetchEnabledConfigCatFeatures();
   }
} satisfies FeatureStoreType);

export default featuresStore;
